import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, StatusBadge, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import FilesPanel from "@/components/FilesPanel";
import { ContractDialog } from "@/components/ContractDialog";
import { ArrowLeft, EnvelopeSimple, Phone, MapPin, IdentificationBadge, Receipt, FileText, ChatCircleDots, Briefcase, Copy, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: client, isLoading } = useQuery({ queryKey: ["client", id], queryFn: async () => (await api.get(`/clients/${id}`)).data });
  const { data: allProjects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: comms = [] } = useQuery({ queryKey: ["comms", "client", id], queryFn: async () => (await api.get(`/communications?client_id=${id}`)).data });
  const { data: allInvoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: allContracts = [] } = useQuery({ queryKey: ["contracts"], queryFn: async () => (await api.get("/contracts")).data });

  const projects = allProjects.filter((p) => p.client_id === id);
  const invoices = allInvoices.filter((i) => i.client_id === id);

  // Active (unsigned) sign links for THIS client — so ops can text/WhatsApp them.
  const activeSignLinks = allContracts
    .filter((c) => c.client_id === id && c.kind === "client" && c.signing_token && !c.signed_at)
    .map((c) => {
      const p = allProjects.find((x) => x.project_id === c.project_id);
      return {
        contract_id: c.contract_id,
        contract_number: c.contract_number,
        project_title: p?.title || "(unknown project)",
        sign_url: `${window.location.origin}/sign/client/${c.signing_token}`,
        created_at: c.created_at,
        language: c.language,
      };
    });

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Sign link copied — paste into text or WhatsApp");
    } catch {
      window.prompt("Copy this sign link:", url);
    }
  };
  const smsHref = (url) => `sms:?&body=${encodeURIComponent(`Please review and sign your interpreter agreement here: ${url}`)}`;
  const waHref = (phone, url) => {
    const digits = (phone || "").replace(/\D/g, "");
    return `https://wa.me/${digits}?text=${encodeURIComponent(`Please review and sign your interpreter agreement here: ${url}`)}`;
  };

  const totalBilled = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);

  if (isLoading) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!client) return <div className="p-10 text-slate-400">Client not found.</div>;

  return (
    <div data-testid="client-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/clients")} className="mb-4" data-testid="back-to-clients">
        <ArrowLeft size={14} className="mr-1.5" /> Back to clients
      </Button>

      <PageHeader
        eyebrow={client.org_type}
        title={client.name}
        description={client.contact_name ? `Primary contact: ${client.contact_name}` : "Client account overview"}
      />

      {/* Header info card */}
      <div className="card-flat p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Info icon={<EnvelopeSimple size={14} />} label="Email" value={client.email} />
        <Info icon={<Phone size={14} />} label="Phone" value={client.phone} />
        <Info icon={<MapPin size={14} />} label="Address" value={client.address} />
        <Info icon={<IdentificationBadge size={14} />} label="Alien Reg #" value={client.alien_number} mono />
      </div>

      {/* Active sign links — for texting / WhatsApp */}
      {activeSignLinks.length > 0 && (
        <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-4 mb-6" data-testid="active-sign-links">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[11px] uppercase tracking-widest font-bold text-amber-900">Awaiting signature</div>
            <div className="text-xs text-amber-800">· {activeSignLinks.length} active sign link{activeSignLinks.length > 1 ? "s" : ""}</div>
          </div>
          <div className="space-y-2">
            {activeSignLinks.map((s) => (
              <div key={s.contract_id} className="bg-white border border-amber-200 rounded-md p-3" data-testid={`sign-link-${s.contract_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-semibold text-sm text-slate-900">{s.project_title}</div>
                    <div className="text-[11px] text-slate-500">{s.contract_number} · sent {fmtDate(s.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                  <FileText size={13} className="text-slate-400 shrink-0" />
                  <input
                    readOnly
                    value={s.sign_url}
                    onFocus={(e) => e.target.select()}
                    className="text-[12px] font-mono bg-transparent flex-1 outline-none text-slate-700 truncate"
                    data-testid={`sign-link-input-${s.contract_id}`}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyLink(s.sign_url)}
                    className="h-7 px-2 shrink-0"
                    data-testid={`sign-link-copy-${s.contract_id}`}
                  >
                    <Copy size={12} className="mr-1" /> Copy
                  </Button>
                </div>
                <div className="flex gap-2 mt-2">
                  <a
                    href={smsHref(s.sign_url)}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100"
                    data-testid={`sign-link-sms-${s.contract_id}`}
                  >
                    <ChatText size={12} /> Text via SMS
                  </a>
                  {client.phone && (
                    <a
                      href={waHref(client.phone, s.sign_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100"
                      data-testid={`sign-link-whatsapp-${s.contract_id}`}
                    >
                      <ChatCircleDots size={12} /> WhatsApp {client.phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPI label="Projects" value={projects.length} />
        <KPI label="Invoices" value={invoices.length} />
        <KPI label="Billed" value={fmtMoney(totalBilled)} />
        <KPI label="Paid" value={fmtMoney(totalPaid)} accent="emerald" />
      </div>

      <Tabs defaultValue="projects" data-testid="client-tabs">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="projects" data-testid="tab-projects"><Briefcase size={14} className="mr-1.5" /> Projects · {projects.length}</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comms"><ChatCircleDots size={14} className="mr-1.5" /> Communications · {comms.length}</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt size={14} className="mr-1.5" /> Invoices · {invoices.length}</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files"><FileText size={14} className="mr-1.5" /> Files</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-4">
          {projects.length === 0 ? <Empty text="No projects yet." /> : (
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-5 py-2">Project</th><th className="text-left px-3 py-2">Scheduled</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-5 py-2">Fee</th></tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.project_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-2">
                        <div className="font-medium text-slate-900">{p.title}</div>
                        <div className="text-xs text-slate-500">{p.project_type} · {p.language}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{fmtDate(p.scheduled_at)}</td>
                      <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                      <td className="px-5 py-2 text-right font-mono">{fmtMoney((p.client_rate || 0) * (p.duration_hours || 1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <Link to="/projects"><Button size="sm" variant="outline">Manage in Projects →</Button></Link>
          </div>
        </TabsContent>

        <TabsContent value="comms" className="mt-4">
          {comms.length === 0 ? <Empty text="No communications logged yet." /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {comms.map((c) => (
                <div key={c.comm_id} className="card-flat p-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="uppercase tracking-wider font-semibold text-blue-800">{c.kind} · {c.direction}</span>
                    <span className="text-slate-400">{fmtDate(c.created_at)}</span>
                  </div>
                  {c.subject && <div className="font-display font-semibold text-slate-900 mt-1">{c.subject}</div>}
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap line-clamp-5">{c.body}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          {invoices.length === 0 ? <Empty text="No invoices yet." /> : (
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-5 py-2">Invoice #</th><th className="text-left px-3 py-2">Issued</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-5 py-2">Amount</th></tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.invoice_id} className="border-t border-slate-100">
                      <td className="px-5 py-2 font-mono">{i.invoice_number}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{fmtDate(i.issued_at)}</td>
                      <td className="px-3 py-2"><StatusBadge status={i.status} /></td>
                      <td className="px-5 py-2 text-right font-mono">{fmtMoney(i.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <div className="card-flat p-5">
            <FilesPanel entityType="client" entityId={id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ icon, label, value, mono }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{icon}{label}</div>
      <div className={`text-sm text-slate-900 mt-1 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function KPI({ label, value, accent = "slate" }) {
  const tones = { emerald: "text-emerald-700", slate: "text-slate-900" };
  return (
    <div className="card-flat p-4">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${tones[accent]}`}>{value}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="card-flat p-8 text-center text-sm text-slate-400">{text}</div>;
}
